import { useMemo } from "react";
import { createNanoIconSet } from "react-native-nano-icons";
import Animated from "react-native-reanimated";

import useConfig from "$src/core/hooks/use-config";
import type { NanoGlyphMapInput } from "$src/core/models/icon";

import $ from "./Icon.web.styles";
import type { AnimatedIconProps, IconProps } from "./types";

let cached: ReturnType<typeof createNanoIconSet<NanoGlyphMapInput>> | null =
  null;
let cachedHash: string | undefined;

const getIconComponent = (icons: NanoGlyphMapInput) => {
  if (cached && cachedHash === icons.m.h) return cached;
  cachedHash = icons.m.h;
  cached = createNanoIconSet<NanoGlyphMapInput>(icons);
  return cached;
};

// nano-icons renders a DOM span, so Unistyles classes never reach it.
// 100cqh fills the wrapper, whose CSS width/height Unistyles does resolve.
const GLYPH_FILL = {
  width: "100%",
  height: "100%",
  fontSize: "100cqh",
} as const;

const IconBase = ({ name, style }: IconProps) => {
  const { icons } = useConfig();

  const IconComponent = useMemo(() => getIconComponent(icons), [icons]);

  return (
    <Animated.View style={[$.container, style]}>
      {/* eslint-disable-next-line react-hooks/static-components */}
      <IconComponent
        name={name}
        color={typeof style.color === "string" ? style.color : undefined}
        style={GLYPH_FILL}
      />
    </Animated.View>
  );
};

/**
 * Web has no UI-thread colour driver, so the animated colour is dropped and the
 * icon renders statically from `style.color`.
 */
const AnimatedIconBase = ({ name, style }: AnimatedIconProps) => {
  if (__DEV__) {
    console.warn(
      "[Icon.Animated] Colour animation is not supported on web; rendering a static icon.",
    );
  }

  return <IconBase name={name} style={style} />;
};

export default Object.assign(IconBase, {
  Animated: AnimatedIconBase,
});
